import { call, put, select, takeEvery } from 'redux-saga/effects';
import { transactionActions } from '../transactionsSlice';
import { tableControlActions } from 'store/tableControlSlice/tableControlSlice';
import { selectAllTablesData } from 'store/tableControlSlice/tableControlSelectors';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { TRANSACTION_PAGE_TABS } from 'config';
import { selectIsVideoWallEnabled, selectTransactions, selectTransactionsSplitViewMode } from '../transactionsSelectors';
import { unpinAllPinnedLanes } from './stickyLaneSagas';
import { replayInventoryValidationsSkippedInSplitView } from 'store/formControlSlice/formControlSagas/inventoryValidationSplitView';

const {
    DESKS_TAB: { ID: desksTabId },
    VIDEO_WALL_TAB: { ID: videoWallTabId },
    TRANSACTIONS_SPLIT_VIEW_TAB: { ID: transactionsSplitViewTabId },
} = TRANSACTION_PAGE_TABS;

export function* toggleTransactionsSplitViewMode() {
    yield put(transactionActions.setChangingOperationalModeInProgress('Changing transactions view mode...'));
    const currentState = yield select(selectTransactionsSplitViewMode);
    const allTransactions = yield select(selectTransactions);
    const isVideoWallEnabled = yield select(selectIsVideoWallEnabled);
    const splitViewModeEnabled = !currentState;

    // Sticky lanes are a tab-view concept; entering split view releases all pinned lanes for this desk.
    if (splitViewModeEnabled) {
        yield call(unpinAllPinnedLanes);
    }

    yield call([cameraStreamManager, cameraStreamManager.closeAllTabsExcept], videoWallTabId);
    yield put(transactionActions.setTransactionsSplitViewModeEnabled(splitViewModeEnabled));
    if (!splitViewModeEnabled) {
        yield put(transactionActions.clearSplitViewActiveTransaction());
        yield call(replayInventoryValidationsSkippedInSplitView);
    }
    yield put(tableControlActions.resetAllHistoryQueries());

    /**
     * TABLE DATA FETCHING STRATEGY WHEN TOGGLING TRANSACTION VIEW MODE
     *
     * Before this block we call `resetAllHistoryQueries()` which clears server-side
     * history query state (searchTerm / timeRange / filters) kept in tableControlSlice.
     * SplitView's `HistoryTable` reads those via `selectHistoryQueryByTableId(tableId)`,
     * so resetting here prevents stale filters from leaking across mode switches.
     *
     * SPLIT SCREEN VIEW:
     * - History table uses its OWN server-side data source (`useHistoryDataSource` +
     *   `fetchLocationHistory`) inside `HistoryTable` (rendered via the `serverSideQuery`
     *   flag on `LocationTransactionsTable`). It does NOT go through
     *   `tableControlActions.fetchTableDataStart` and does NOT store rows in
     *   `tableControlSlice.allTablesData`. Pagination, search and filtering are all done
     *   on the backend, per panel mount.
     * - The history table also fetches its own columns config per location
     *   (`useHistoryColumnsConfig` + `ensureHistoryColumnsConfig`, cached in
     *   `tableControlSlice.historyColumnsConfig`), in both views.
     * - Manual announcements are fetched eagerly per transaction by `useTopicData`
     *   (on `SplitViewTransactionCard` mount), not on a topic-button click.
     * - Appointments are fetched eagerly per transaction by `useTopicData` as well.
     * - Consequently, when SplitView is active we never pre-fetch the classic-view
     *   `singleLocationTransactions` table config / per-location manual announcements
     *   through the saga path used in `handleDeskAssignedMessage`.
     *
     * OLD/CLASSIC TRANSACTION VIEW:
     * - In `handleDeskAssignedMessage` we dispatch
     *   `fetchTableDataStart({ tableId: locationId, ... })` for each assigned
     *   transaction's location.
     * - For `singleLocationTransactions` the saga sets `skipDataFetch = true` and there is
     *   no `configName`, so neither the history list nor its columns config is
     *   pre-fetched here. What IS pre-fetched is manual announcements for
     *   `${locationId}_manualAnnouncements` (only when `gateDirection === 'OUT'`, via
     *   `fetchLocationAnnouncements`).
     * - Subsequent pages of history are then loaded on-demand by `ClientTable`'s
     *   `optionChangedHandler` (`fetchTableDataStart({ append: true })`).
     *
     * SWITCHING FROM SPLIT VIEW TO CLASSIC VIEW (this block):
     * - Some `locationId`s may have never gone through the classic saga path while
     *   SplitView was active, so their columns config and (for OUT gates) manual
     *   announcements may not be primed in `tableControlSlice`.
     * - We deduplicate by `locationId` (multiple transactions on the same location
     *   share the same pre-fetch) and skip locations that already have an entry in
     *   `allTablesData[locationId]` (the saga writes `data: []` even when
     *   `skipDataFetch` is true, so presence of the key is a reliable proxy for
     *   "this location has already been primed").
     * - This restores the classic view's expectation that columns config exists and,
     *   for OUT gates, manual announcements are loaded — it does NOT preload the
     *   actual history rows (those still load on demand via the grid's pager).
     */
    if (!splitViewModeEnabled && allTransactions?.length) {
        const allTablesData = yield select(selectAllTablesData);
        const locationIdsFetched = new Set();

        for (const transaction of allTransactions) {
            const { locationId, gateDirection } = transaction;

            if (locationIdsFetched.has(locationId)) continue;
            const hasTableData = !!allTablesData[locationId];

            if (!hasTableData) {
                yield put(
                    tableControlActions.fetchTableDataStart({
                        tableId: locationId,
                        locationId,
                        pageNum: 0,
                        fetchLocationAnnouncements: gateDirection === 'OUT',
                    })
                );
                locationIdsFetched.add(locationId);
            }
        }
    }

    let tabId;
    if (splitViewModeEnabled) tabId = transactionsSplitViewTabId;
    else {
        switch (true) {
            case !!allTransactions?.at(0)?.tabId:
                tabId = allTransactions.at(0)?.tabId;
                break;
            case !!isVideoWallEnabled:
                tabId = videoWallTabId;
                break;
            default:
                tabId = desksTabId;
        }
    }
    yield put(transactionActions.setActiveTab(tabId));

    yield put(transactionActions.setChangingOperationalModeInProgress(false));
}

export function* onToggleTransactionsSplitViewMode() {
    yield takeEvery(transactionActions.toggleTransactionsSplitViewMode, toggleTransactionsSplitViewMode);
}
